import type { ProjectExecute, ProjectTemplate } from "./types/ead-types.js";
import { isLiveProjectExecute, syncCanonicalRunFields } from "./ui/run-status.ts";

function isStorage(value: unknown): value is Storage {
  return (
    Boolean(value) &&
    typeof (value as Storage).getItem === "function" &&
    typeof (value as Storage).setItem === "function"
  );
}

export function getSafeLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    try {
      return isStorage(window.localStorage) ? window.localStorage : null;
    } catch {
      return null;
    }
  }
  return null;
}

// Project Run History Management
const MAX_PROJECT_RUNS = 120;
const STORAGE_KEY_EXECUTIONS = "ead_executions_v2";
const STORAGE_KEY_EXECUTIONS_LEGACY = "ead_executions";
const STORAGE_KEY_PFM_NODE_REPORTS = "ead_pfm_node_reports";
const STORAGE_KEY_PFM_NODE_REPORT_PENDING = "ead_pfm_node_report_pending_v1";
const STORAGE_KEY_PFM_VISITED_NODES = "ead_pfm_visited_nodes_v1";

export type SavedPfmNodeReport = {
  executionId: string;
  sessionKey: string;
  nodeKey: string;
  title: string;
  content: string;
  generatedAt: number;
};

export type PendingPfmNodeReportRequest = {
  reqId: string;
  childReqId?: string;
  executionId: string;
  sessionKey: string;
  nodeKey: string;
  title: string;
  sentAt: number;
  prompt: string;
  /** Full baseline body for no_new_findings restore (prompt may include a truncated snippet). */
  baselineMarkdown?: string;
};

export type SavedVisitedPfmNode = {
  nodeKey: string;
  title: string;
};

type SavedVisitedPfmContext = {
  visitedNodeKeys: string[];
  visitedNodes?: SavedVisitedPfmNode[];
  activeNodeKey: string;
  updatedAt: number;
};

function pfmNodeReportStorageKey(executionId: string, nodeKey: string): string {
  return `${executionId}::${nodeKey}`;
}

function pfmVisitedContextStorageKey(executionId: string, contextCacheKey: string): string {
  return `${executionId}::${contextCacheKey}`;
}

function normalizeNodeKey(value: string): string {
  return String(value || "").trim().toLowerCase();
}

function loadVisitedPfmContextMap(): Record<string, SavedVisitedPfmContext> {
  const storage = getSafeLocalStorage();
  if (!storage) return {};
  try {
    const data = storage.getItem(STORAGE_KEY_PFM_VISITED_NODES);
    if (!data) return {};
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, SavedVisitedPfmContext>)
      : {};
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to load visited PFM nodes from localStorage:", error);
    return {};
  }
}

function saveVisitedPfmContextMap(map: Record<string, SavedVisitedPfmContext>): void {
  const storage = getSafeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_PFM_VISITED_NODES, JSON.stringify(map));
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to save visited PFM nodes to localStorage:", error);
  }
}

function normalizeVisitedNodes(entry: SavedVisitedPfmContext | undefined): SavedVisitedPfmNode[] {
  if (!entry) return [];
  if (Array.isArray(entry.visitedNodes) && entry.visitedNodes.length) {
    const seen = new Set<string>();
    const out: SavedVisitedPfmNode[] = [];
    for (const raw of entry.visitedNodes) {
      const nodeKey = normalizeNodeKey(String(raw?.nodeKey || ""));
      if (!nodeKey || seen.has(nodeKey)) continue;
      seen.add(nodeKey);
      const title = String(raw?.title || nodeKey).trim() || nodeKey;
      out.push({ nodeKey, title });
    }
    return out;
  }
  if (!Array.isArray(entry.visitedNodeKeys)) return [];
  return entry.visitedNodeKeys
    .map((nodeKey) => normalizeNodeKey(nodeKey))
    .filter(Boolean)
    .map((nodeKey) => ({ nodeKey, title: nodeKey }));
}

export function loadVisitedPfmPath(
  executionId: string,
  contextCacheKey: string,
): SavedVisitedPfmNode[] {
  if (!executionId || !contextCacheKey) return [];
  const key = pfmVisitedContextStorageKey(executionId, contextCacheKey);
  return normalizeVisitedNodes(loadVisitedPfmContextMap()[key]);
}

export function loadVisitedPfmNodes(executionId: string, contextCacheKey: string): string[] {
  return loadVisitedPfmPath(executionId, contextCacheKey).map((entry) => entry.nodeKey);
}

export function loadActivePfmNode(executionId: string, contextCacheKey: string): string | null {
  if (!executionId || !contextCacheKey) return null;
  const key = pfmVisitedContextStorageKey(executionId, contextCacheKey);
  const entry = loadVisitedPfmContextMap()[key];
  const activeNodeKey = normalizeNodeKey(entry?.activeNodeKey || "");
  return activeNodeKey || null;
}

export function saveVisitedPfmNode(
  executionId: string,
  contextCacheKey: string,
  nodeKey: string,
  title = "",
): void {
  const cleanNodeKey = normalizeNodeKey(nodeKey);
  if (!executionId || !contextCacheKey || !cleanNodeKey) return;
  const map = loadVisitedPfmContextMap();
  const key = pfmVisitedContextStorageKey(executionId, contextCacheKey);
  const current = map[key];
  const prior = normalizeVisitedNodes(current);
  const label = String(title || "").trim() || cleanNodeKey;
  const existingIdx = prior.findIndex((entry) => entry.nodeKey === cleanNodeKey);
  const nextNodes = existingIdx >= 0
    ? prior.map((entry, idx) => (
        idx === existingIdx ? { nodeKey: cleanNodeKey, title: label } : entry
      ))
    : [...prior, { nodeKey: cleanNodeKey, title: label }];
  map[key] = {
    visitedNodeKeys: nextNodes.map((entry) => entry.nodeKey),
    visitedNodes: nextNodes,
    activeNodeKey: cleanNodeKey,
    updatedAt: Date.now(),
  };
  saveVisitedPfmContextMap(map);
}

function loadPfmNodeReportMap(): Record<string, SavedPfmNodeReport> {
  const storage = getSafeLocalStorage();
  if (!storage) return {};
  try {
    const data = storage.getItem(STORAGE_KEY_PFM_NODE_REPORTS);
    if (!data) return {};
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as Record<string, SavedPfmNodeReport>
      : {};
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to load PFM node reports from localStorage:", error);
    return {};
  }
}

function savePfmNodeReportMap(reports: Record<string, SavedPfmNodeReport>): void {
  const storage = getSafeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_PFM_NODE_REPORTS, JSON.stringify(reports));
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to save PFM node reports to localStorage:", error);
  }
}

export function loadPfmNodeReport(executionId: string, nodeKey: string): SavedPfmNodeReport | null {
  if (!executionId || !nodeKey) return null;
  return loadPfmNodeReportMap()[pfmNodeReportStorageKey(executionId, nodeKey)] ?? null;
}

export function savePfmNodeReport(report: SavedPfmNodeReport): void {
  if (!report.executionId || !report.nodeKey) return;
  const reports = loadPfmNodeReportMap();
  reports[pfmNodeReportStorageKey(report.executionId, report.nodeKey)] = report;
  savePfmNodeReportMap(reports);
}

function loadPendingPfmNodeReportRequestMap(): Record<string, PendingPfmNodeReportRequest> {
  const storage = getSafeLocalStorage();
  if (!storage) return {};
  try {
    const data = storage.getItem(STORAGE_KEY_PFM_NODE_REPORT_PENDING);
    if (!data) return {};
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, PendingPfmNodeReportRequest>)
      : {};
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to load pending PFM node report requests:", error);
    return {};
  }
}

function savePendingPfmNodeReportRequestMap(map: Record<string, PendingPfmNodeReportRequest>): void {
  const storage = getSafeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_PFM_NODE_REPORT_PENDING, JSON.stringify(map));
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to save pending PFM node report requests:", error);
  }
}

export function loadPendingPfmNodeReportRequest(
  executionId: string,
  nodeKey: string,
): PendingPfmNodeReportRequest | null {
  if (!executionId || !nodeKey) return null;
  const pending = loadPendingPfmNodeReportRequestMap()[pfmNodeReportStorageKey(executionId, nodeKey)];
  if (!pending || typeof pending.reqId !== "string" || typeof pending.sentAt !== "number") return null;
  return pending;
}

export function savePendingPfmNodeReportRequest(request: PendingPfmNodeReportRequest): void {
  if (!request.executionId || !request.nodeKey || !request.reqId) return;
  const map = loadPendingPfmNodeReportRequestMap();
  map[pfmNodeReportStorageKey(request.executionId, request.nodeKey)] = request;
  savePendingPfmNodeReportRequestMap(map);
}

export function clearPendingPfmNodeReportRequest(executionId: string, nodeKey: string): void {
  if (!executionId || !nodeKey) return;
  const map = loadPendingPfmNodeReportRequestMap();
  const key = pfmNodeReportStorageKey(executionId, nodeKey);
  if (!(key in map)) return;
  delete map[key];
  savePendingPfmNodeReportRequestMap(map);
}

// PFM Mindmap pending request correlation
const STORAGE_KEY_PFM_PENDING_MINDMAP = "ead-pfm-pending-mindmap-req-v1";

export type PendingPfmRequest = {
  /** Storage scope key. Top-level mindmaps use the run sessionKey, drill-down
   *  mindmaps pass a composite cacheKey such as `${sessionKey}::path::A>B`. */
  reqId: string;
  sessionKey: string;
  sentAtSec: number;
  prompt: string;
};

function loadPendingPfmRequestMap(): Record<string, PendingPfmRequest> {
  const storage = getSafeLocalStorage();
  if (!storage) return {};
  try {
    const data = storage.getItem(STORAGE_KEY_PFM_PENDING_MINDMAP);
    if (!data) return {};
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, PendingPfmRequest>)
      : {};
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to load pending PFM mindmap requests:", error);
    return {};
  }
}

function savePendingPfmRequestMap(map: Record<string, PendingPfmRequest>): void {
  const storage = getSafeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_PFM_PENDING_MINDMAP, JSON.stringify(map));
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to save pending PFM mindmap requests:", error);
  }
}

export function loadPendingPfmRequest(sessionKey: string): PendingPfmRequest | null {
  if (!sessionKey) return null;
  const entry = loadPendingPfmRequestMap()[sessionKey];
  if (!entry || typeof entry.reqId !== "string" || typeof entry.sentAtSec !== "number") {
    return null;
  }
  return entry;
}

export function savePendingPfmRequest(req: PendingPfmRequest): void {
  if (!req.sessionKey || !req.reqId) return;
  const map = loadPendingPfmRequestMap();
  map[req.sessionKey] = req;
  savePendingPfmRequestMap(map);
}

export function clearPendingPfmRequest(sessionKey: string): void {
  if (!sessionKey) return;
  const map = loadPendingPfmRequestMap();
  if (!(sessionKey in map)) return;
  delete map[sessionKey];
  savePendingPfmRequestMap(map);
}

// PFM Mindmap: last valid generated result per run/session.
const STORAGE_KEY_PFM_MINDMAPS = "ead-pfm-mindmaps-v1";

export type SavedPfmMindmapNode = {
  index: number;
  key: string;
  title: string;
};

export type SavedPfmMindmap = {
  sessionKey: string;
  title: string;
  description: string;
  nodes: SavedPfmMindmapNode[];
  savedAt: number;
};

function loadPfmMindmapMap(): Record<string, SavedPfmMindmap> {
  const storage = getSafeLocalStorage();
  if (!storage) return {};
  try {
    const data = storage.getItem(STORAGE_KEY_PFM_MINDMAPS);
    if (!data) return {};
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, SavedPfmMindmap>)
      : {};
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to load saved PFM mindmaps:", error);
    return {};
  }
}

function savePfmMindmapMap(map: Record<string, SavedPfmMindmap>): void {
  const storage = getSafeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_PFM_MINDMAPS, JSON.stringify(map));
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to save PFM mindmaps:", error);
  }
}

export function loadPfmMindmap(sessionKey: string): SavedPfmMindmap | null {
  if (!sessionKey) return null;
  const saved = loadPfmMindmapMap()[sessionKey];
  if (!saved || !Array.isArray(saved.nodes) || saved.nodes.length === 0) return null;
  return saved;
}

export function savePfmMindmap(mindmap: SavedPfmMindmap): void {
  if (!mindmap.sessionKey || !Array.isArray(mindmap.nodes) || mindmap.nodes.length === 0) return;
  const map = loadPfmMindmapMap();
  map[mindmap.sessionKey] = mindmap;
  savePfmMindmapMap(map);
}

export function clearPfmMindmap(sessionKey: string): void {
  if (!sessionKey) return;
  const map = loadPfmMindmapMap();
  if (!(sessionKey in map)) return;
  delete map[sessionKey];
  savePfmMindmapMap(map);
}

/**
 * Cleanup old project runs, keeping only the most recent MAX_PROJECT_RUNS
 */
export function cleanupOldRuns(executions: ProjectExecute[]): ProjectExecute[] {
  if (!Array.isArray(executions)) {
    return [];
  }

  const isLiveRun = (run: ProjectExecute): boolean => isLiveProjectExecute(run);

  const runOrder = (run: ProjectExecute): number => {
    const runNo = Number(run.runNumber || 0);
    if (Number.isFinite(runNo) && runNo > 0) return runNo;
    const created = Number(
      (run as { createdAt?: number; created_at?: number }).createdAt ??
        (run as { created_at?: number }).created_at ??
        0,
    );
    if (Number.isFinite(created) && created > 0) return created;
    const started = Number(run.startTime || 0);
    if (Number.isFinite(started) && started > 0) return started;
    return 0;
  };

  const byId = new Map<string, ProjectExecute>();
  for (const run of executions) {
    const id = String(run.id || "").trim();
    if (!id) continue;
    const prev = byId.get(id);
    if (!prev || runOrder(run) >= runOrder(prev)) {
      byId.set(id, run);
    }
  }

  // Keep live rows even when startTime is null; sort by canonical run order.
  const cleaned = [...byId.values()]
    .filter((run) => run.id && (isLiveRun(run) || Number(run.startTime || 0) > 0))
    .sort((a, b) => runOrder(b) - runOrder(a))
    .slice(0, MAX_PROJECT_RUNS);

  // Log cleanup if any runs were removed
  if (cleaned.length < executions.length) {
    console.log(`[EAD_ExpUI] Cleaned up ${executions.length - cleaned.length} old run(s). Kept ${cleaned.length}/${MAX_PROJECT_RUNS} most recent.`);
  }

  return cleaned;
}

/**
 * Save executions to localStorage with automatic cleanup
 */
export function saveExecutions(executions: ProjectExecute[]): void {
  const storage = getSafeLocalStorage();
  if (!storage) return;

  const cleaned = cleanupOldRuns(executions);
  try {
    storage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(cleaned));
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to save executions to localStorage:", error);
    // If storage is full, try saving with even fewer runs
    try {
      const emergencyCleanup = cleaned.slice(0, Math.floor(MAX_PROJECT_RUNS / 2));
      storage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(emergencyCleanup));
    } catch (retryError) {
      console.error("[EAD_ExpUI] Emergency cleanup also failed:", retryError);
    }
  }
}

/**
 * Load executions from localStorage
 */
function normalizeCachedExecution(run: ProjectExecute): ProjectExecute {
  return syncCanonicalRunFields({ ...run });
}

export function loadExecutions(): ProjectExecute[] {
  const storage = getSafeLocalStorage();
  if (!storage) return [];

  try {
    let data = storage.getItem(STORAGE_KEY_EXECUTIONS);
    if (!data) {
      const legacy = storage.getItem(STORAGE_KEY_EXECUTIONS_LEGACY);
      if (legacy) {
        data = legacy;
        storage.setItem(STORAGE_KEY_EXECUTIONS, legacy);
        storage.removeItem(STORAGE_KEY_EXECUTIONS_LEGACY);
      }
    }
    if (data) {
      const parsed = JSON.parse(data) as ProjectExecute[];
      return cleanupOldRuns(parsed.map(normalizeCachedExecution));
    }
  } catch (error) {
    console.warn("[EAD_ExpUI] Failed to load executions from localStorage:", error);
  }

  return [];
}

/**
 * Add a new execution and cleanup old ones automatically
 */
export function addExecution(execution: ProjectExecute): ProjectExecute[] {
  const current = loadExecutions();
  const updated = [execution, ...current];
  const cleaned = cleanupOldRuns(updated);
  saveExecutions(cleaned);
  return cleaned;
}

/**
 * Remove a specific execution by ID
 */
export function removeExecution(executionId: string): ProjectExecute[] {
  const current = loadExecutions();
  const filtered = current.filter((e) => e.id !== executionId);
  saveExecutions(filtered);
  return filtered;
}

/**
 * Update an existing execution
 */
export function updateExecution(executionId: string, updates: Partial<ProjectExecute>): ProjectExecute[] {
  const current = loadExecutions();
  const index = current.findIndex((e) => e.id === executionId);

  if (index !== -1) {
    current[index] = { ...current[index], ...updates };
    saveExecutions(current);
  }

  return current;
}
